import { useState } from 'react';

/**
 * The kiosk bearer token, read once from `location.hash` — `KioskCheckinPage.tsx`'s own header
 * explains why a fragment rather than a query string. Read via a lazy `useState` initialiser (runs
 * exactly once, on first render) rather than `useEffect`, so the very first render already has the
 * token — an effect-based read would render one extra "loading" frame with no token yet, for no
 * benefit, since reading `location.hash` is synchronous and side-effect-free.
 *
 * Deliberately does **not** strip the fragment from the URL after reading it. The token stays visible
 * in the address bar for the tablet's entire check-in session — this matches the deployment model
 * (`emr-po` A16 decision 1: staff hands the tablet over with the link already loaded, nobody else
 * reads that screen), and it means an accidental page reload recovers the same session instead of
 * stranding the patient on a blank "no token" screen: `location.hash` survives a reload, plain React
 * state does not.
 */
const TOKEN_PATTERN = /(?:^|&)token=([^&]+)/;

function readTokenFromHash(): string | null {
  const raw = window.location.hash;
  const hash = raw.startsWith('#') ? raw.slice(1) : raw;
  const match = TOKEN_PATTERN.exec(hash);
  if (match?.[1] === undefined) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed percent-encoding in the fragment — treat exactly like "no token", the same
    // fail-closed shape `requireKioskToken` on the server gives a malformed `Authorization` header.
    return null;
  }
}

export function useKioskToken(): string | null {
  const [token] = useState(readTokenFromHash);
  return token;
}
